import { useState, useRef, useEffect, useCallback } from 'react';
import Map, { Source, Layer, Marker, Popup, NavigationControl } from 'react-map-gl';
import type { LayerProps } from 'react-map-gl';
import type { FeatureCollection, Point } from 'geojson';
import { IEstablishment } from '../../interfaces/establishment';
import { getEstablishment } from '../../interfaces/establishments';
import { EstablishmentLogo } from '../franchise/EstablishmentLogo';
import { PhoneIcon, EnvelopeIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { FaLinkedin } from 'react-icons/fa';
import 'mapbox-gl/dist/mapbox-gl.css';

interface SignalMapProps {
    signals: IEstablishment[];
    onSaveToggle: (id: string, saved: boolean) => void;
}

type EstWithCoords = IEstablishment & { coordinates: { lat: number; lng: number } };

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibWF0aGlldWR1Y2tmbG93IiwiYSI6ImNtajc5cnQ1aDAxeGEzcHNhZWtub3I5N3QifQ.BaEcgEbT0TKEYgeBX0KwVw';

const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
    'paris': { lat: 48.8566, lng: 2.3522 },
    'marseille': { lat: 43.2965, lng: 5.3698 },
    'lyon': { lat: 45.7640, lng: 4.8357 },
    'toulouse': { lat: 43.6047, lng: 1.4442 },
    'nice': { lat: 43.7102, lng: 7.2620 },
    'nantes': { lat: 47.2184, lng: -1.5536 },
    'montpellier': { lat: 43.6108, lng: 3.8767 },
    'strasbourg': { lat: 48.5734, lng: 7.7521 },
    'bordeaux': { lat: 44.8378, lng: -0.5792 },
    'lille': { lat: 50.6292, lng: 3.0573 },
    'rennes': { lat: 48.1173, lng: -1.6778 },
    'reims': { lat: 49.2583, lng: 4.0317 },
    'saint-étienne': { lat: 45.4397, lng: 4.3872 },
    'toulon': { lat: 43.1242, lng: 5.9280 },
    'le havre': { lat: 49.4944, lng: 0.1079 },
    'grenoble': { lat: 45.1885, lng: 5.7245 },
    'dijon': { lat: 47.3220, lng: 5.0415 },
    'angers': { lat: 47.4784, lng: -0.5632 },
    'nîmes': { lat: 43.8367, lng: 4.3601 },
    'clermont-ferrand': { lat: 45.7772, lng: 3.0870 },
    'aix-en-provence': { lat: 43.5297, lng: 5.4474 },
    'brest': { lat: 48.3900, lng: -4.4861 },
    'tours': { lat: 47.3941, lng: 0.6848 },
    'amiens': { lat: 49.8941, lng: 2.2958 },
    'rouen': { lat: 49.4432, lng: 1.0993 },
    'nancy': { lat: 48.6921, lng: 6.1844 },
    'metz': { lat: 49.1193, lng: 6.1757 },
    'caen': { lat: 49.1829, lng: -0.3707 },
    'avignon': { lat: 43.9493, lng: 4.8055 },
    'perpignan': { lat: 42.6886, lng: 2.8948 },
    'besançon': { lat: 47.2380, lng: 6.0243 },
    'orléans': { lat: 47.9029, lng: 1.9093 },
    'mulhouse': { lat: 47.7508, lng: 7.3359 },
    'limoges': { lat: 45.8336, lng: 1.2611 },
    'annecy': { lat: 45.8992, lng: 6.1294 },
    'versailles': { lat: 48.8049, lng: 2.1204 },
    'boulogne-billancourt': { lat: 48.8356, lng: 2.2410 },
    'nanterre': { lat: 48.8925, lng: 2.2069 },
    'créteil': { lat: 48.7903, lng: 2.4555 },
    'montreuil': { lat: 48.8634, lng: 2.4448 },
    'argenteuil': { lat: 48.9475, lng: 2.2469 },
    'saint-denis': { lat: 48.9362, lng: 2.3574 },
    'roubaix': { lat: 50.6942, lng: 3.1746 },
    'tourcoing': { lat: 50.7236, lng: 3.1609 },
    'dunkerque': { lat: 51.0343, lng: 2.3768 },
    'vitry-sur-seine': { lat: 48.7873, lng: 2.3933 },
    'poitiers': { lat: 46.5802, lng: 0.3404 },
    'asnières-sur-seine': { lat: 48.9145, lng: 2.2869 },
};

function getCoordinatesForCity(city?: string): { lat: number; lng: number } | null {
    if (!city) return null;
    const normalized = city.toLowerCase().trim();
    for (const [key, coords] of Object.entries(CITY_COORDS)) {
        if (normalized.includes(key)) return coords;
    }
    return null;
}

type Region = {
    key: string;
    label: string;
    viewport: { latitude: number; longitude: number; zoom: number };
    contains: (c: { lat: number; lng: number }) => boolean;
};

const REGIONS: Region[] = [
    { key: 'metro', label: 'Métropole', viewport: { latitude: 46.603354, longitude: 1.888334, zoom: 5 }, contains: c => c.lat >= 41 && c.lat <= 51.5 && c.lng >= -5.5 && c.lng <= 10 },
    { key: 'reunion', label: 'Réunion', viewport: { latitude: -21.1, longitude: 55.5, zoom: 9 }, contains: c => c.lat >= -22 && c.lat <= -20 && c.lng >= 54 && c.lng <= 57 },
    { key: 'martinique', label: 'Martinique', viewport: { latitude: 14.65, longitude: -61.0, zoom: 10 }, contains: c => c.lat >= 14 && c.lat <= 15.5 && c.lng >= -62 && c.lng <= -60 },
    { key: 'guadeloupe', label: 'Guadeloupe', viewport: { latitude: 16.25, longitude: -61.58, zoom: 10 }, contains: c => c.lat >= 15.5 && c.lat <= 17 && c.lng >= -62.5 && c.lng <= -60.8 },
    { key: 'mayotte', label: 'Mayotte', viewport: { latitude: -12.8, longitude: 45.2, zoom: 11 }, contains: c => c.lat >= -13.5 && c.lat <= -12 && c.lng >= 44.5 && c.lng <= 46 },
    { key: 'guyane', label: 'Guyane', viewport: { latitude: 4.0, longitude: -53.0, zoom: 7 }, contains: c => c.lat >= 2 && c.lat <= 6 && c.lng >= -55 && c.lng <= -51 },
    { key: 'nouvelle-caledonie', label: 'Nouvelle-Calédonie', viewport: { latitude: -21.3, longitude: 165.6, zoom: 7 }, contains: c => c.lat >= -23 && c.lat <= -19 && c.lng >= 163 && c.lng <= 168 },
    { key: 'polynesie', label: 'Polynésie', viewport: { latitude: -17.5, longitude: -149.6, zoom: 8 }, contains: c => c.lat >= -19 && c.lat <= -16 && c.lng >= -151 && c.lng <= -148 },
];

// --- Cluster layer styles (transitions make splitting smooth) ---
const clusterLayer: LayerProps = {
    id: 'clusters',
    type: 'circle',
    source: 'establishments',
    filter: ['has', 'point_count'],
    paint: {
        'circle-color': [
            'step', ['get', 'point_count'],
            '#818cf8', 10,
            '#4f46e5', 50,
            '#3730a3'
        ],
        'circle-radius': [
            'step', ['get', 'point_count'],
            18, 10,
            26, 50,
            34
        ],
        'circle-radius-transition': { duration: 300, delay: 0 },
        'circle-color-transition': { duration: 300, delay: 0 },
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.92,
        'circle-opacity-transition': { duration: 300, delay: 0 },
    }
};

const clusterCountLayer: LayerProps = {
    id: 'cluster-count',
    type: 'symbol',
    source: 'establishments',
    filter: ['has', 'point_count'],
    layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 13,
    },
    paint: {
        'text-color': '#ffffff',
        'text-opacity-transition': { duration: 300, delay: 0 },
    }
};
// ----------------------------------------------------------------

const SIGNAL_TYPE_COLORS: Record<string, string> = {
    restaurant_opening: '#4f46e5',
    franchise_signing: '#a855f7',
    multi_site: '#22c55e',
    restaurant_owner_change: '#3b82f6',
    network_expansion: '#14b8a6',
};

const SIGNAL_TYPE_LABELS: Record<string, string> = {
    restaurant_opening: 'Ouverture',
    franchise_signing: 'Franchise signée',
    multi_site: 'Nouvel établissement',
    restaurant_owner_change: 'Nouveau gérant',
    network_expansion: 'Expansion réseau',
};

export default function SignalMap({ signals: establishments }: SignalMapProps) {
    const [selectedEst, setSelectedEst] = useState<EstWithCoords | null>(null);
    const [selectedEstDetail, setSelectedEstDetail] = useState<IEstablishment | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [viewport, setViewport] = useState({ latitude: 46.603354, longitude: 1.888334, zoom: 5 });
    const [activeRegion, setActiveRegion] = useState('metro');
    const [unclusteredIds, setUnclusteredIds] = useState<Set<string>>(new Set());
    const mapRef = useRef<any>(null);

    // Build list of establishments with coordinates
    const established: EstWithCoords[] = establishments
        .map(est => {
            const geo = est.address?.geo?.coordinates;
            if (geo && geo.length === 2) {
                return { ...est, coordinates: { lat: geo[1], lng: geo[0] } };
            }
            const coords = getCoordinatesForCity(est.address?.city);
            if (!coords) return null;
            return { ...est, coordinates: coords };
        })
        .filter(Boolean) as EstWithCoords[];

    // Only show tabs for regions that have at least one establishment
    const availableRegions = REGIONS.filter(r => established.some(e => r.contains(e.coordinates)));

    // Points filtered to active region
    const visibleEstablishments = established.filter(e => {
        const region = REGIONS.find(r => r.key === activeRegion);
        return region ? region.contains(e.coordinates) : true;
    });

    // GeoJSON for Mapbox native clustering
    const geojsonData: FeatureCollection<Point> = {
        type: 'FeatureCollection',
        features: visibleEstablishments.map(est => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [est.coordinates.lng, est.coordinates.lat]
            },
            properties: {
                id: est._id,
                color: SIGNAL_TYPE_COLORS[(est as any).signals?.[0]?.type] || '#6b7280',
            }
        }))
    };

    // Re-query which source features are currently unclustered whenever the map moves
    const refreshUnclusteredIds = useCallback(() => {
        const map = mapRef.current?.getMap();
        if (!map || !map.getSource('establishments')) return;
        const features = map.querySourceFeatures('establishments', { sourceLayer: '' });
        const ids = new Set<string>();
        features.forEach((f: any) => {
            if (!f.properties?.cluster && f.properties?.id) {
                ids.add(f.properties.id);
            }
        });
        setUnclusteredIds(ids);
    }, []);

    const onClick = useCallback((event: any) => {
        const map = mapRef.current?.getMap();
        if (!map) return;

        const features = map.queryRenderedFeatures(event.point, {
            layers: ['clusters']
        });

        if (!features.length) return;

        const feature = features[0];
        const clusterId = feature.properties?.cluster_id;
        const source = map.getSource('establishments') as any;
        source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
            if (err) return;
            map.easeTo({
                center: (feature.geometry as any).coordinates,
                zoom,
                duration: 400
            });
        });
    }, []);

    useEffect(() => {
        if (established.length > 0) {
            const metro = REGIONS.find(r => r.key === 'metro')!;
            const metroEsts = established.filter(e => metro.contains(e.coordinates));
            if (metroEsts.length > 0) {
                setViewport(metro.viewport);
            }
        }
    }, [establishments.length]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRegionChange = (regionKey: string) => {
        setActiveRegion(regionKey);
        setSelectedEst(null);
        const region = REGIONS.find(r => r.key === regionKey);
        if (region) setViewport(region.viewport);
    };

    const getTimeAgo = (dateString?: string) => {
        if (!dateString) return null;
        const diffMs = Date.now() - new Date(dateString).getTime();
        const mins = Math.floor(diffMs / 60000);
        const hours = Math.floor(diffMs / 3600000);
        const days = Math.floor(diffMs / 86400000);
        if (mins < 60) return `Il y a ${mins} min`;
        if (hours < 24) return `Il y a ${hours}h`;
        if (days < 7) return `Il y a ${days}j`;
        return new Date(dateString).toLocaleDateString("fr-FR", { month: "short", day: "numeric", year: "numeric" });
    };


    if (established.length === 0) {
        return (
            <div className="flex items-center justify-center h-96 bg-gray-50 rounded-xl border border-gray-200">
                <div className="text-center">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">Aucune localisation disponible</h3>
                    <p className="text-sm text-[#6B6B6B]">Les établissements affichés n'ont pas de coordonnées géographiques.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .mapboxgl-popup-close-button { font-size: 16px; line-height: 1; padding: 3px 7px; right: 6px; top: 6px; color: #9ca3af; background: rgba(255,255,255,0.9); border-radius: 6px; z-index: 10; }
                .mapboxgl-popup-close-button:hover { background: #f3f4f6; color: #374151; }
                .mapboxgl-popup-content { padding: 0 !important; border-radius: 16px !important; overflow: hidden; box-shadow: 0 8px 32px rgba(0,0,0,0.18) !important; }
                .map-popup-scroll::-webkit-scrollbar { width: 4px; }
                .map-popup-scroll::-webkit-scrollbar-track { background: transparent; }
                .map-popup-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
                @keyframes markerPop {
                    0%   { transform: scale(0.4); opacity: 0; }
                    70%  { transform: scale(1.15); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
            {availableRegions.length > 1 && (
                <div className="flex gap-1 mb-2">
                    {availableRegions.map(r => (
                        <button
                            key={r.key}
                            onClick={() => handleRegionChange(r.key)}
                            className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                                activeRegion === r.key
                                    ? 'bg-black text-white border-black'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            )}
            <div className="h-[600px] w-full rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                <Map
                    ref={mapRef}
                    {...viewport}
                    onMove={evt => setViewport(evt.viewState)}
                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                    mapboxAccessToken={MAPBOX_TOKEN}
                    style={{ width: '100%', height: '100%' }}
                    interactiveLayerIds={['clusters']}
                    onClick={onClick}
                    onMouseEnter={() => { mapRef.current?.getMap()?.getCanvas && (mapRef.current.getMap().getCanvas().style.cursor = 'pointer'); }}
                    onMouseLeave={() => { mapRef.current?.getMap()?.getCanvas && (mapRef.current.getMap().getCanvas().style.cursor = ''); }}
                    onMoveEnd={refreshUnclusteredIds}
                    onZoomEnd={refreshUnclusteredIds}
                    onSourceData={(e: any) => { if (e.sourceId === 'establishments' && e.isSourceLoaded) refreshUnclusteredIds(); }}
                >
                    <NavigationControl position="top-right" />

                    <Source
                        id="establishments"
                        type="geojson"
                        data={geojsonData}
                        cluster={true}
                        clusterMaxZoom={14}
                        clusterRadius={50}
                    >
                        <Layer {...clusterLayer} />
                        <Layer {...clusterCountLayer} />
                    </Source>

                    {/* Logo markers for unclustered individual points */}
                    {visibleEstablishments
                        .filter(est => unclusteredIds.has(est._id as string))
                        .map(est => {
                            const signalType = (est as any).signals?.[0]?.type;
                            const color = SIGNAL_TYPE_COLORS[signalType] || '#6b7280';
                            const isSelected = selectedEst?._id === est._id;
                            return (
                                <Marker key={est._id} latitude={est.coordinates.lat} longitude={est.coordinates.lng} anchor="bottom">
                                    <button
                                        className="cursor-pointer focus:outline-none"
                                        style={{ transform: isSelected ? 'scale(1.25)' : 'scale(1)', transition: 'transform 0.2s ease' }}
                                        onClick={e => {
                                        e.stopPropagation();
                                        setSelectedEst(est);
                                        setSelectedEstDetail(null);
                                        if (est._id) {
                                            setDetailLoading(true);
                                            getEstablishment(est._id as string).then(d => {
                                                setSelectedEstDetail(d);
                                                setDetailLoading(false);
                                            }).catch(() => setDetailLoading(false));
                                        }
                                    }}
                                    >
                                        <div
                                            className="w-8 h-8 rounded-full border-2 shadow-lg flex items-center justify-center overflow-hidden"
                                            style={{
                                                borderColor: color,
                                                backgroundColor: color,
                                                animation: 'markerPop 0.25s ease-out',
                                            }}
                                        >
                                            <EstablishmentLogo name={est.name || ''} franchise={est.franchise} emojiClassName="text-base leading-none" />
                                        </div>
                                    </button>
                                </Marker>
                            );
                        })
                    }

                    {selectedEst && (() => {
                        const signal = (selectedEst as any).signals?.[0];
                        const signalType = signal?.type as string | undefined;
                        const signalColor = SIGNAL_TYPE_COLORS[signalType || ''] || '#6b7280';
                        const signalLabel = SIGNAL_TYPE_LABELS[signalType || ''] || signalType || '';
                        const timeAgo = getTimeAgo(signal?.date);

                        const rating = selectedEst.ratings?.source_summary?.[0];
                        const totalSeats = (selectedEst.operations?.seats_inside || 0) + (selectedEst.operations?.seats_terrace || 0);

                        const detailData = selectedEstDetail || selectedEst;
                        const person = (detailData as any).peoples?.[0] as any;
                        const enrichments: any[] = person?.enrichments || [];
                        const personPhone = enrichments.find((e: any) => e.type === 'phone' && (e.status === 'done' || e.status === 'approved'))?.data?.phone ?? null;
                        const personEmail = enrichments.find((e: any) => (e.type === 'email' || e.type === 'email_company') && (e.status === 'done' || e.status === 'approved'))?.data?.email ?? null;
                        const personLinkedIn = enrichments.find((e: any) => e.type === 'linkedin' && (e.status === 'done' || e.status === 'approved'))?.data?.url ?? null;
                        const contactPhone = personPhone || detailData.phone;
                        const contactEmail = personEmail || detailData.email;

                        const cuisineDisplay = selectedEst.cuisine_types?.[0] || selectedEst.categories?.[0];

                        return (
                            <Popup
                                latitude={selectedEst.coordinates.lat}
                                longitude={selectedEst.coordinates.lng}
                                onClose={() => { setSelectedEst(null); setSelectedEstDetail(null); }}
                                closeOnClick={false}
                                anchor="top"
                                maxWidth="280px"
                            >
                                <div style={{ width: '260px' }}>

                                    {/* Header */}
                                    <div className="p-3">
                                        <div className="flex items-start gap-2">
                                            <div className="w-9 h-9 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                <EstablishmentLogo name={selectedEst.name || ''} franchise={selectedEst.franchise} emojiClassName="text-base" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-[#1A1A1A] text-xs leading-snug truncate">{selectedEst.name}</h3>
                                                <p className="text-xs text-[#6B6B6B] truncate mt-0.5">{selectedEst.address?.city}</p>
                                                {/* Stats row */}
                                                <div className="flex items-center gap-2 mt-1">
                                                    {rating?.rating_avg && (
                                                        <span className="flex items-center gap-0.5 text-xs text-gray-600">
                                                            <span className="text-yellow-400 text-xs">★</span>
                                                            {rating.rating_avg.toFixed(1)}
                                                        </span>
                                                    )}
                                                    {totalSeats > 0 && <span className="text-xs text-gray-400">{totalSeats} 🪑</span>}
                                                    {cuisineDisplay && <span className="text-xs text-gray-400 truncate">{cuisineDisplay}</span>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Signal pill */}
                                        {signal && (
                                            <div className="mt-2 flex items-center gap-1.5 px-2 py-1 rounded-md" style={{ backgroundColor: signalColor + '15' }}>
                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 animate-pulse" style={{ backgroundColor: signalColor }} />
                                                <span className="text-xs font-semibold flex-1" style={{ color: signalColor }}>{signalLabel}</span>
                                                {timeAgo && <span className="text-xs text-gray-400">{timeAgo}</span>}
                                            </div>
                                        )}
                                    </div>

                                    {/* Contact */}
                                    {detailLoading && (
                                        <div className="border-t border-gray-100 px-3 py-2 flex items-center gap-2 text-xs text-gray-400">
                                            <svg className="animate-spin w-3 h-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                                            Chargement...
                                        </div>
                                    )}
                                    {!detailLoading && (contactPhone || contactEmail || personLinkedIn) && (
                                        <div className="border-t border-gray-100 px-3 py-2 flex flex-col gap-1.5">
                                            {person && (person.first_name || person.last_name) && (
                                                <span className="text-xs text-gray-600 font-medium truncate">{[person.first_name, person.last_name].filter(Boolean).join(' ')}</span>
                                            )}
                                            {contactPhone && (
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-xs font-semibold text-gray-900">{contactPhone}</span>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(contactPhone).catch(() => {}); }}
                                                            title="Copier le numéro"
                                                            className="px-2 py-1 text-[10px] font-semibold bg-gray-900 text-white hover:bg-gray-700 rounded transition-colors"
                                                        >
                                                            Copier
                                                        </button>
                                                        <a href={`tel:${contactPhone}`} onClick={e => e.stopPropagation()} title="Appeler"
                                                           className="w-6 h-6 flex items-center justify-center rounded bg-green-50 text-green-700 hover:bg-green-100 transition-colors">
                                                            <PhoneIcon className="w-3 h-3" />
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1">
                                                {contactEmail && (
                                                    <a href={`mailto:${contactEmail}`} onClick={e => e.stopPropagation()} title="Email"
                                                       className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                                                        <EnvelopeIcon className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                                {personLinkedIn && (
                                                    <a href={personLinkedIn} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="LinkedIn"
                                                       className="w-7 h-7 flex items-center justify-center rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                                                        <FaLinkedin className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* CTA */}
                                    <div className="border-t border-gray-100 px-3 py-2.5">
                                        <a
                                            href={`/establishments/${selectedEst._id}`}
                                            onClick={e => e.stopPropagation()}
                                            className="flex items-center justify-center gap-1 w-full bg-black text-white text-xs font-semibold py-2 rounded-lg hover:bg-gray-800 transition-colors"
                                        >
                                            Voir la fiche
                                            <ChevronRightIcon className="w-3 h-3" />
                                        </a>
                                    </div>

                                </div>
                            </Popup>
                        );
                    })()}
                </Map>
            </div>
        </>
    );
}
